import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import {
  PostingRequirementsDef,
  requirementsKeyOf,
} from './posting-requirements/requirements-key';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useRemoveOpeningFromPosting } from '@/components/pages/job-posting/use-remove-opening-from-posting';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Typography } from '@/components/ui/typography';
import { getProgress } from '@/utils/progress';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { ZIPRECRUITER } from '@/types/channels';
import { useToaster } from '@/components/base/toaster';
import { TrashIcon } from '@/components/icons/trash';

import { PostingRequirements } from './posting-requirements';
import { SideNav, SelectedSection } from './side-nav';
import { useOpeningProgress } from './hooks/use-opening-progress';
import { useUpdateOpeningDescription } from './hooks/use-update-opening-description';
import { useValidatePostingRequirements } from './hooks/use-validate-posting-requirements';
import { useSectionSnapshot } from './hooks/use-section-snapshot';
import { Separator } from '@/components/ui/separator';
import { BaseFields } from './base-fields';
import { hiringAddressOf } from './hiring-address';
import { buildOpeningUpdateBody } from './opening-update-body';

interface ExpandedViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  contractRequirements: PostingRequirementsDef[];
  toggleExpanded?: () => void;
  onRequestRefreshBanner?: () => void;
}

export function ExpandedView({
  opening,
  payDisplay,
  contractRequirements,
  toggleExpanded: _toggleExpanded,
  onRequestRefreshBanner,
}: ExpandedViewProps) {
  const intl = useIntl();
  const { notifyWarning } = useToaster();
  const { hookForm, selectedOpeningIds, selectedChannelSlugs } =
    useJobPostingContext();
  const { setValue, getValues, trigger, watch } = hookForm;
  const channels = watch('channels');
  const postingTitle = watch(`jobDetails.${opening.external_id}.title`);
  const isZipRecruiterSelected = selectedChannelSlugs.includes(ZIPRECRUITER);
  const isStateCodeRequired = isZipRecruiterSelected && opening.remote === true;

  const removeOpeningFromPosting = useRemoveOpeningFromPosting(
    opening.external_id,
  );
  const { isInVonqChannel, totalRequiredCount, filledRequiredCount } =
    useOpeningProgress({ opening, contractRequirements });

  const [selectedSection, setSelectedSection] =
    useState<SelectedSection>('common');
  const [optionalExpandedByKey, setOptionalExpandedByKey] = useState<
    Record<string, boolean>
  >({});

  const selectedChannel =
    selectedSection !== 'common' ? channels?.[selectedSection] : null;
  const selectedContract = contractRequirements.find(
    def => def.key === requirementsKeyOf(selectedChannel?.vonqDetails),
  );

  const collapseOpening = () => {
    setValue(`jobDetails.${opening.external_id}.isExpanded`, false);
  };

  const { updateOpeningDescriptionAsync, isUpdatingOpening } =
    useUpdateOpeningDescription();
  const {
    validateAllContractsForOpeningAsync,
    isValidatingPostingRequirements,
  } = useValidatePostingRequirements();

  const { snapshotSection, handleCancelClick } = useSectionSnapshot({
    openingExternalId: opening.external_id,
    hookForm,
    savedAddress: hiringAddressOf(opening),
    selectedSection,
    selectedChannel,
    channels,
    collapseOpening,
  });

  // Snapshot the new section on entry so Cancel can revert only the fields
  // the user edited there. All values stay in RHF until Save is clicked.
  const handleSelectSection = (newSection: SelectedSection) => {
    if (newSection === selectedSection) return;
    snapshotSection(newSection);
    setSelectedSection(newSection);
  };

  // Fall back to "Common fields" when the selected contract/channel is
  // deleted out from under the card (e.g. via the "Selected Openings"
  // panel) — otherwise neither selectedChannel nor selectedContract resolve
  // anymore and the right panel renders blank.
  useEffect(() => {
    if (
      selectedSection !== 'common' &&
      !(selectedChannel && selectedContract)
    ) {
      snapshotSection('common');
      setSelectedSection('common');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, selectedChannel, selectedContract]);

  const handleSaveClick = async () => {
    const isValid = await trigger(`jobDetails.${opening.external_id}`);
    if (!isValid) {
      notifyWarning({
        message: intl.formatMessage({
          defaultMessage: 'Please fill in all required fields',
          description: 'Error message for incomplete job details',
        }),
      });
      return;
    }

    // Validate all Vonq contract fields against the backend before saving.
    const isServerValid = await validateAllContractsForOpeningAsync(
      opening.external_id,
    ).catch(() => false);

    if (!isServerValid) {
      notifyWarning({
        message: intl.formatMessage({
          defaultMessage:
            'Some posting requirement fields have errors. Please review and correct them.',
          description:
            'Warning shown when server-side posting requirements validation finds errors',
        }),
      });
      return;
    }

    try {
      await updateOpeningDescriptionAsync({
        openingExternalId: opening.external_id,
        requestBody: buildOpeningUpdateBody(
          getValues(`jobDetails.${opening.external_id}`),
          opening,
        ),
      });
      // Keep the common snapshot in sync with what was just saved so a
      // subsequent Cancel reverts to the saved state, not the pre-open state.
      snapshotSection('common');
      collapseOpening();
    } catch {
      // Error toast is shown by the hook's onError
    }
  };

  return (
    <div className="border-fx-neutral-200 flex flex-col rounded-lg border bg-white shadow-sm">
      {/* Header: opening title + progress */}
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex flex-col gap-0.5">
          <Typography variant="caption" className="text-fx-neutral-400">
            <FormattedMessage
              defaultMessage="Reviewing Opening"
              description="Label above the opening title in the expanded job card header"
            />
          </Typography>
          <Typography variant="medium2" className="text-fx-neutral-600">
            {postingTitle}
          </Typography>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <Typography variant="body1" className="text-fx-neutral-400">
            <FormattedMessage
              defaultMessage="{filled} of {total} required fields"
              description="Progress counter showing how many required fields have been filled"
              values={{
                filled: Math.max(0, filledRequiredCount),
                total: totalRequiredCount,
              }}
            />
          </Typography>
          <Progress
            rootClassName="w-44 h-1"
            className="bg-fx-primary"
            value={getProgress(
              totalRequiredCount,
              Math.max(0, filledRequiredCount),
            )}
          />
        </div>
      </div>

      <Separator />

      {/* Two-column body */}
      <div className="flex">
        {/* Left nav panel */}
        {contractRequirements.length > 0 && (
          <div className="border-fx-neutral-200 w-[213px] shrink-0 border-r">
            <SideNav
              openingId={opening.external_id}
              contractRequirements={contractRequirements}
              selectedSection={selectedSection}
              onSelectSection={handleSelectSection}
              isInVonqChannel={isInVonqChannel}
              isStateCodeRequired={isStateCodeRequired}
              isRemote={opening.remote === true}
            />
          </div>
        )}

        {/* Right content panel */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-6 px-6 py-4">
            {selectedSection === 'common' ? (
              <BaseFields
                opening={opening}
                payDisplay={payDisplay}
                isInVonqChannel={isInVonqChannel}
                isZipRecruiterSelected={isZipRecruiterSelected}
                onRequestRefreshBanner={onRequestRefreshBanner}
              />
            ) : selectedContract && selectedChannel ? (
              <PostingRequirements
                openingId={opening.external_id}
                contract={selectedContract}
                channelDisplayTitle={selectedChannel.displayTitle ?? undefined}
                logoRectangleUrl={selectedChannel.vonqDetails?.logoRectangleUrl}
                productId={selectedChannel.vonqDetails?.productId}
                boardName={selectedChannel.vonqDetails?.channelName}
                showOptional={
                  optionalExpandedByKey[selectedContract.key] ?? false
                }
                onShowOptionalChange={value =>
                  setOptionalExpandedByKey(prev => ({
                    ...prev,
                    [selectedContract.key]: value,
                  }))
                }
              />
            ) : null}
          </div>

          <Separator />

          {/* Action buttons — always visible regardless of selected section */}
          {selectedOpeningIds.length > 1 && (
            <div className="px-6 py-4">
              <div className="flex justify-between gap-6">
                <Button
                  type="button"
                  variant="ghost"
                  className="!text-fx-neutral-600"
                  onClick={removeOpeningFromPosting}
                >
                  <TrashIcon />
                  <FormattedMessage
                    defaultMessage="Remove from posting"
                    description="Remove the opening from the job posting"
                  />
                </Button>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleCancelClick()}
                  >
                    <FormattedMessage
                      defaultMessage="Cancel"
                      description="Cancel the channel changes"
                    />
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleSaveClick()}
                    disabled={
                      isValidatingPostingRequirements || isUpdatingOpening
                    }
                  >
                    {(isValidatingPostingRequirements || isUpdatingOpening) && (
                      <Loader2 className="animate-spin" />
                    )}
                    <FormattedMessage
                      defaultMessage="Save"
                      description="Save the channel changes"
                    />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
